import { useState } from 'react';
import { motion } from 'framer-motion';
import { testimonials, getCommunityTestimonials, getFastingTestimonials } from '../data/testimonials';
import TestimonialCard from '../components/ui/TestimonialCard';
import * as api from '../lib/api';
import { VIDEOS } from '../lib/media';
import HeroVideo from '../components/HeroVideo';

export default function Join() {
  const communityTestimonials = getCommunityTestimonials().slice(0, 6);
  const fastingTestimonials = getFastingTestimonials().slice(0, 3);
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleJoinClick = async () => {
    if (!email || !email.includes('@')) {
      setError('Please enter a valid email address');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const affiliateCode = sessionStorage.getItem('ref_code') || undefined;
      const result = await api.billing.publicCheckout(email.trim().toLowerCase(), affiliateCode);

      if (result.checkoutUrl) {
        window.location.href = result.checkoutUrl;
      } else {
        setError('Unable to start checkout. Please try again.');
      }
    } catch (err: any) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {/* Hero - Pricing Card */}
      <section className="page-hero page-hero--full">
        <HeroVideo className="page-hero-video" src={VIDEOS.meatHero} />
        <div className="page-hero-overlay" />
        <div className="container">
          <div className="pricing-card">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="pricing-card-title">
                You Found Your Tribe
              </h2>
              <p className="pricing-card-subtitle">
                Join thousands who finally found their people.
              </p>

              <p className="pricing-card-price">$97</p>
              <p className="pricing-card-term">
                One-time payment · Lifetime access
              </p>

              <input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setError(null); }}
                className="join-email-input"
              />

              {error && (
                <p className="join-error-text">
                  {error}
                </p>
              )}

              <button
                onClick={handleJoinClick}
                disabled={isLoading}
                className={`btn btn-primary btn-lg btn-full-width${isLoading ? ' opacity-loading' : ''}`}
              >
                {isLoading ? 'Processing...' : 'Join the Community'}
              </button>

              <p className="pricing-card-members">
                Active 24/7 · Lifetime access
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Social Proof Bar */}
      <section className="join-social-proof-bar">
        <div className="container">
          <div className="join-social-proof-inner">
            <span>Four Books Included</span>
            <span>Private Community</span>
            <span>Lifetime Access</span>
            <span>Active 24/7</span>
          </div>
        </div>
      </section>

      {/* We Hear You */}
      <section className="section">
        <div className="container">
          <div className="content-narrow">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <p className="eyebrow-label">
                We Hear You
              </p>
              <h2 className="heading-clamp-md mb-xl">
                We know how you feel. We've been there too.
              </h2>
            </motion.div>

            <div className="join-struggle-grid">
              {[
                "You're tired of explaining yourself at every meal",
                "You feel like nobody in your life understands",
                "You know this works and you're ready to commit",
                "You have questions at 2am and nobody to ask",
                "You've been looking for people who get it",
                "You wonder if you're doing this right"
              ].map((struggle, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                  className="join-struggle-card"
                >
                  {struggle}
                </motion.div>
              ))}
            </div>

            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="join-you-belong"
            >
              You're in the right place.
            </motion.p>
          </div>
        </div>
      </section>

      {/* The Difference */}
      <section className="section section-dark">
        <div className="container">
          <div className="content-narrow">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="join-dark-heading">
                This Is What Changes Everything
              </h2>
              <p className="join-dark-subheading">
                Having people who get it.
              </p>
              <p className="join-dark-body">
                People who celebrate your wins. Who answer your questions at 2am because they've
                been there. Who make you feel normal instead of weird. Who understand exactly
                what you're going through.
              </p>
              <p className="join-dark-body-last">
                That's what a tribe does. That's what makes the difference between trying something
                and actually making it your life.
              </p>
              <p className="join-dark-closing">
                You found us. We've been waiting for you.<br />
                Let's do this together.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Testimonial */}
      <section className="section">
        <div className="container">
          <motion.div
            className="content-narrow join-testimonial-block"
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
          >
            <p className="join-testimonial-quote">
              "The community is why I'm still doing this. Without them, I would have quit months ago.
              When you have people cheering you on who actually understand what you're going through, everything changes."
            </p>
            <p className="join-testimonial-author">
              — David K., Denver, CO
            </p>
          </motion.div>
        </div>
      </section>

      {/* What Changes */}
      <section className="section section-light">
        <div className="container">
          <div className="content-narrow">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <h2 className="heading-clamp-md mb-lg">
                What If You Had People Who Get It?
              </h2>
              <p className="join-light-body">
                People who don't need an explanation. Who celebrate your wins without the skeptical looks.
                Who've been exactly where you are. Who make you feel normal instead of weird.
              </p>
              <p className="join-light-closing">
                That's not a feature. That's a lifeline.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* The Community Benefits */}
      <section className="section">
        <div className="container">
          <div className="section-header">
            <h2>Your Tribe Is Waiting</h2>
          </div>

          <div className="grid-2 mx-auto-mid gap-lg">
            {[
              {
                title: 'A Safe Space',
                description: "No judgment. No explaining. Just people who understand because they're living it too. Ask any question. Share any struggle. You belong here."
              },
              {
                title: 'Real Support',
                description: "Questions at 2am? Someone's there. Bad day? People who've been there. Wins to celebrate? We're all here for that too. You're never alone."
              },
              {
                title: 'Belonging',
                description: "Stop feeling like the weird one. Find your people. Finally belong somewhere where you're not the outsider—you're just one of us."
              },
              {
                title: 'The Difference',
                description: "Between quitting when it gets hard and thriving because you have people in your corner. Community is the difference maker."
              }
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="card"
              >
                <div className="card-content">
                  <h3 className="join-benefit-title">
                    {item.title}
                  </h3>
                  <p className="join-benefit-desc">
                    {item.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Community Testimonials */}
      <section className="section section-light">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="heading-section text-center mb-md">
              What Members Say
            </h2>
            <p className="join-subtitle-center">
              Real people who found their tribe.
            </p>
          </motion.div>

          <div className="grid-3 mx-auto-wider gap-lg">
            {communityTestimonials.map((testimonial, index) => (
              <TestimonialCard
                key={testimonial.id}
                testimonial={testimonial}
                index={index}
                variant="default"
              />
            ))}
          </div>
        </div>
      </section>

      {/* Value Stack */}
      <section className="section">
        <div className="container">
          <div className="section-header">
            <h2>When You Join, You Get Everything</h2>
            <p className="join-section-desc">
              The community is the heart of it all. But we're also including everything you need to thrive.
            </p>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="join-value-stack"
          >
            <div className="join-value-header">
              <p className="join-value-header-text">
                Everything Included
              </p>
            </div>

            <div className="join-value-body">
              {[
                { title: 'Lifetime Community Access', description: 'Your tribe, forever. 24/7 support from people who get it.' },
                { title: 'Fasting Carnivore Book', description: 'The complete guide with real transformation stories.' },
                { title: 'Nano Fasting Book', description: '15 levels to fasting mastery. The gradual approach that sticks.' },
                { title: 'Quick Start Guides', description: 'Step-by-step protocols to get started immediately.' },
                { title: '30-Day Trackers', description: 'Track your progress and stay accountable.' },
                { title: 'All Bonus Materials', description: 'Cheat sheets, troubleshooting guides, and more.' }
              ].map((item, index) => (
                <div
                  key={index}
                  className={index < 5 ? 'join-value-item-border' : 'join-value-item'}
                >
                  <h4 className="join-value-item-title">
                    {item.title}
                  </h4>
                  <p className="join-value-item-desc">
                    {item.description}
                  </p>
                </div>
              ))}
            </div>

            <div className="join-value-footer">
              <p className="join-price-display">
                $97
              </p>
              <p className="join-price-term">
                One-time payment · Lifetime access
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Mid-Page CTA */}
      <section className="section section-dark">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="content-narrow text-center"
          >
            <h2 className="join-cta-heading">
              Ready to Find Your People?
            </h2>
            <p className="join-cta-subtext">
              Join thousands who are thriving together. Your tribe is here.
            </p>
            <p className="join-cta-price">
              $97
            </p>
            <p className="join-cta-term">
              One-time payment · Lifetime access
            </p>
            <button
              onClick={() => {
                if (!email) {
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                  return;
                }
                handleJoinClick();
              }}
              disabled={isLoading}
              className={`btn btn-secondary btn-lg${isLoading ? ' opacity-loading' : ''}`}
            >
              {isLoading ? 'Processing...' : 'Join the Community'}
            </button>
          </motion.div>
        </div>
      </section>

      {/* Fasting Testimonials */}
      <section className="section">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="heading-section text-center mb-md">
              Fasting Becomes Easy
            </h2>
            <p className="join-subtitle-center">
              When you have support, everything changes.
            </p>
          </motion.div>

          <div className="grid-3 mx-auto-wide gap-lg">
            {fastingTestimonials.map((testimonial, index) => (
              <TestimonialCard
                key={testimonial.id}
                testimonial={testimonial}
                index={index}
                variant="quote-only"
              />
            ))}
          </div>
        </div>
      </section>

      {/* Objection Handling */}
      <section className="section section-light">
        <div className="container">
          <div className="content-narrow">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="heading-section text-center">You Might Be Thinking...</h2>
            </motion.div>

            <div className="grid-stack mt-xl">
              {[
                {
                  question: '"I can find free communities online..."',
                  answer: "You can. But are they active? Supportive? Full of people actually committed to this? Free communities are often ghost towns or full of people who tried for a week. This is different—it's people who invested in their success, who show up every day."
                },
                {
                  question: '"I\'m not sure carnivore is right for me..."',
                  answer: "That's exactly why the community matters. Ask questions, learn from people who've been doing this for years, and get support as you find your own path. We're all figuring it out together."
                },
                {
                  question: '"$97 is a lot of money..."',
                  answer: "It's a one-time payment for lifetime access. Compare that to monthly subscriptions that add up to hundreds per year, or courses that cost $500+. This is your tribe, forever. No recurring fees."
                },
                {
                  question: '"What if I don\'t fit in?"',
                  answer: "Our members range from 25 to 70+. Complete beginners and multi-year veterans. Different backgrounds, different reasons. What unites them? They all felt like outsiders before. They found their people. So will you."
                }
              ].map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                  className="join-objection-card"
                >
                  <p className="join-objection-question">
                    {item.question}
                  </p>
                  <p className="join-objection-answer">
                    {item.answer}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* More Testimonials */}
      <section className="section">
        <div className="container">
          <h2 className="heading-section text-center mb-xl">
            More Stories From Members
          </h2>
          <div className="grid-3 mx-auto-wider gap-lg">
            {testimonials.slice(10, 16).map((testimonial, index) => (
              <TestimonialCard
                key={testimonial.id}
                testimonial={testimonial}
                index={index}
                variant="quote-only"
              />
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section section-light">
        <div className="container">
          <div className="section-header">
            <h2>Questions</h2>
          </div>
          <div className="content-narrow">
            <FAQItem
              question="What exactly is the community?"
              answer="It's a private space where people doing fasting carnivore support each other. Real people, real conversations, real support. Available 24/7 because someone's always around."
            />
            <FAQItem
              question="I'm just starting out. Is this for me?"
              answer="Especially for you. Starting with people who've been there is how you actually stick with it. We'll help you every step of the way."
            />
            <FAQItem
              question="What if I have questions about carnivore or fasting?"
              answer="That's the whole point. Ask anything, anytime. Someone who's been there will help you out. No question is too basic."
            />
            <FAQItem
              question="Is this a subscription?"
              answer="No. $97 one-time and you're in for life. The community, the books, all of it. Forever."
            />
            <FAQItem
              question="How active is the community?"
              answer="Very. Someone is always around, day or night. It's a real community of real people who show up for each other."
            />
            <FAQItem
              question="Do I get access to everything immediately?"
              answer="Yes. As soon as you join, you get access to the community, both books, all the guides, and bonus materials."
            />
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="cta py-responsive">
        <div className="container text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="join-final-heading">
              Welcome Home
            </h2>
            <p className="join-final-subtext">
              Your tribe is here. Let's do this together.
            </p>
            <p className="join-final-price">
              $97
            </p>
            <p className="join-final-term">
              One-time payment · Lifetime access
            </p>
            <button
              onClick={() => {
                if (!email) {
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                  return;
                }
                handleJoinClick();
              }}
              disabled={isLoading}
              className={`btn btn-secondary btn-lg join-final-btn${isLoading ? ' opacity-loading' : ''}`}
            >
              {isLoading ? 'Processing...' : 'Join the Community'}
            </button>
            <p className="join-final-members">
              Active 24/7 · Lifetime access
            </p>
          </motion.div>
        </div>
      </section>

      {/* PS Section */}
      <section className="section">
        <div className="container">
          <div className="content-narrow">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <p className="join-ps-text">
                <strong className="join-ps-strong">P.S.</strong> — You've made it this far, which tells me you're serious.
                You know carnivore works. You know fasting works. The only question is whether you're going to keep
                waiting, or join thousands of people who are thriving together.
              </p>
            </motion.div>
          </div>
        </div>
      </section>
    </>
  );
}

function FAQItem({ question, answer }: { question: string; answer: string }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="join-faq-item">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="join-faq-button"
      >
        <span>{question}</span>
        <span className="join-faq-icon">
          {isOpen ? '−' : '+'}
        </span>
      </button>
      {isOpen && (
        <div className="join-faq-answer">
          {answer}
        </div>
      )}
    </div>
  );
}
